"use client"

import { useState } from "react"
import { Form, Formik, type FormikHelpers } from "formik"
import { ChevronDown, ChevronUp, HelpCircle, Loader2, Mail } from "lucide-react"
import { toast } from "sonner"

import { FormSelect } from "@/components/form-inputs"
import { Button } from "@/components/ui/button"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { Switch } from "@/components/ui/switch"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { useScheduledSendCreateOptions } from "@/hooks/queries/agent-tasks"
import { SCHEDULED_EMAIL_AGENT_REQUIREMENT } from "@/lib/agent-tasks/scheduled-email-policy"
import { ToolWorkingHoursSetting } from "./tool-working-hours-setting"

export type ScheduleEmailSettings = { enabled?: boolean; email_agent_id?: string; inbox_id?: string; respect_working_hours?: boolean }

/** Selects a tenant-owned Email Agent and mailbox for either source-agent type. */
export function ScheduleEmailToolSettings({ settings, onSave, allowEnable = false, sourceAgentId, disabled = false }: {
  settings?: ScheduleEmailSettings
  onSave: (settings: ScheduleEmailSettings) => Promise<unknown>
  allowEnable?: boolean
  sourceAgentId?: string
  disabled?: boolean
}) {
  const [expanded, setExpanded] = useState(false)
  const [isToggling, setIsToggling] = useState(false)
  const options = useScheduledSendCreateOptions(true, "send_email", "")
  const allBindings = options.data?.pages[0]?.email_bindings ?? []
  const sourceInboxIds = new Set(allBindings.filter(item => item.agent_id === sourceAgentId).map(item => item.inbox_id))
  const bindings = sourceAgentId
    ? allBindings.filter(item => sourceInboxIds.has(item.inbox_id))
    : allBindings
  const agents = options.data?.pages[0]?.email_agents ??
    Array.from(new Map(allBindings.map(item => [item.agent_id, { agent_id: item.agent_id, agent_name: item.agent_name, disabled_reason: null }])).values())
  const sourceAgentReason = agents.find(agent => agent.agent_id === sourceAgentId)?.disabled_reason
  const agentOptions = agents.map(agent => {
    let reason = agent.disabled_reason
    if (!reason && sourceAgentId && !bindings.some(binding => binding.agent_id === agent.agent_id)) {
      reason = sourceAgentReason
        ? `Prepare the current Email Agent first. ${sourceAgentReason}`
        : "Connect a sending mailbox used by the current Email Agent to this agent in its Inbox tab."
    }
    return { value: agent.agent_id, label: agent.agent_name, disabled: Boolean(reason), description: reason ?? undefined,
      action: reason ? { label: "Go to agent", href: `/?page=email-agents&agentId=${encodeURIComponent(agent.agent_id)}` } : undefined }
  })
  const configured = bindings.some(item => item.agent_id === settings?.email_agent_id && item.inbox_id === settings?.inbox_id)
  const staleBinding = Boolean(settings?.email_agent_id && settings?.inbox_id && !configured && !options.isLoading && !options.isError)
  const initialValues = {
    email_agent_id: settings?.email_agent_id ?? "",
    inbox_id: settings?.inbox_id ?? "",
    respect_working_hours: settings?.respect_working_hours === true,
  }

  async function handleSubmit(values: typeof initialValues, helpers: FormikHelpers<typeof initialValues>) {
    const valid = bindings.some(item => item.agent_id === values.email_agent_id && item.inbox_id === values.inbox_id)
    const cleared = !values.email_agent_id && !values.inbox_id
    if (!valid && !cleared) {
      helpers.setStatus("Choose an active Email Agent and connected sending mailbox.")
      return
    }
    try {
      const enabledSettings = { enabled: !cleared }
      await onSave({ email_agent_id: values.email_agent_id, inbox_id: values.inbox_id,
        respect_working_hours: values.respect_working_hours,
        ...enabledSettings })
      helpers.setStatus(undefined)
      toast.success("Email scheduling settings saved.")
    } catch (error) {
      helpers.setStatus(error instanceof Error ? error.message : "Could not save email scheduling settings.")
    }
  }

  async function handleEnabledChange(enabled: boolean) {
    setIsToggling(true)
    try {
      await onSave(enabled ? { ...settings, enabled } : { enabled: false, email_agent_id: "", inbox_id: "", respect_working_hours: false })
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not update email scheduling.")
    } finally {
      setIsToggling(false)
    }
  }

  const settingsForm = (
    <Formik initialValues={initialValues} enableReinitialize onSubmit={handleSubmit}>
      {({ values, setFieldValue, setValues, setStatus, isSubmitting, status }) => (
        <Form className="space-y-3">
          <p className="text-sm text-muted-foreground">The agent can schedule an email from this mailbox after the customer requests it. Delivery follows the Email Agent&apos;s sending policy.</p>
          <FormSelect separateOptions name="email_agent_id" label="Email Agent" placeholder="Choose an agent"
            description={SCHEDULED_EMAIL_AGENT_REQUIREMENT}
            emptyMessage="No Email Agents"
            options={agentOptions}
            onValueChange={() => void setFieldValue("inbox_id", "")} />
          <FormSelect name="inbox_id" label="Sending mailbox" placeholder="Choose a mailbox"
            emptyMessage={values.email_agent_id ? "No mailboxes for this agent" : "Choose an Email Agent first"}
            options={bindings.filter(item => item.agent_id === values.email_agent_id).map(item => ({ value: item.inbox_id, label: item.inbox_address }))} />
          <ToolWorkingHoursSetting id="send-email-working-hours"
            checked={values.respect_working_hours}
            onCheckedChange={checked => void setFieldValue("respect_working_hours", checked)}
            disabled={disabled || isSubmitting} />
          {staleBinding ? <p className="text-sm text-amber-700" role="status">The configured Email Agent cannot send scheduled emails. Choose an eligible agent and mailbox, then save.</p> : null}
          {options.isError ? <p className="text-sm text-destructive">Could not load Email Agent mailboxes.</p> : null}
          {status ? <p className="text-sm" role="status">{status}</p> : null}
          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="outline" disabled={disabled || isSubmitting}
              onClick={() => { void setValues({ email_agent_id: "", inbox_id: "", respect_working_hours: false }); setStatus(undefined) }}>
              <span>Clear settings</span>
            </Button>
            <Button type="submit" disabled={disabled || isSubmitting || options.isLoading || options.isError}>
              {isSubmitting ? <Loader2 aria-hidden="true" className="mr-2 h-4 w-4 animate-spin" /> : null}
              <span>{isSubmitting ? "Saving email scheduling..." : "Save email scheduling"}</span>
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  )

  const settingsContent = options.isLoading ? (
    <div className="flex min-h-32 items-center justify-center gap-2 text-sm text-muted-foreground" role="status">
      <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />
      <span>Loading email scheduling settings...</span>
    </div>
  ) : settingsForm

  if (!allowEnable) return settingsContent

  return (
    <Collapsible open={expanded} onOpenChange={setExpanded}>
      <div className="overflow-hidden rounded-lg border border-violet-200 bg-violet-50">
        <div className="flex items-center justify-between gap-2 p-4">
          <CollapsibleTrigger asChild>
            <button type="button" className="flex min-w-0 flex-1 items-center gap-2 text-left">
              <Mail className="h-4 w-4 shrink-0 text-violet-600" />
              <span className="text-sm font-medium text-gray-700">Send Email</span>
              {expanded
                ? <ChevronUp className="ml-auto mr-2 h-4 w-4 text-gray-500" />
                : <ChevronDown className="ml-auto mr-2 h-4 w-4 text-gray-500" />}
            </button>
          </CollapsibleTrigger>
          <Tooltip>
            <TooltipTrigger asChild>
              <HelpCircle className="h-4 w-4 shrink-0 cursor-help text-gray-400" />
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              Schedule an email from a connected mailbox after the customer requests it.
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Switch
                  aria-label="Enable Send Email"
                  checked={settings?.enabled === true}
                  onCheckedChange={checked => void handleEnabledChange(checked)}
                  disabled={disabled || isToggling || options.isLoading || options.isError || (!settings?.enabled && !configured)}
                />
              </span>
            </TooltipTrigger>
            {!settings?.enabled && !configured ? (
              <TooltipContent>Choose an Email Agent and sending mailbox first.</TooltipContent>
            ) : null}
          </Tooltip>
        </div>
        {!settings?.enabled && !configured ? (
          <p className="px-4 pb-3 text-xs font-medium text-amber-700">
            Choose an Email Agent and sending mailbox, then save email scheduling before enabling this tool.
          </p>
        ) : null}
        <CollapsibleContent>
          <div className="px-4 pb-4">{settingsContent}</div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  )
}
